import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Field } from "./scouting/parts";
import { X, Loader2, Plus, Trash2, ChevronDown, ChevronUp, Grape, FileDown, Scale, Printer, Camera, CloudUpload } from "lucide-react";
import { addPendingFormScan, listPendingFormScans, removePendingFormScan } from "../lib/offline";

const WT = {
  lag_weight: { label: "Lag Phase Weights (lb)", units: 30, avgLabel: "lb/cluster" },
  post_veraison_weight: { label: "Post-Veraison Weights (lb)", units: 30, avgLabel: "lb/cluster" },
};
const TYPE_LABELS = {
  cluster_count: "Cluster Count", shoot_count: "Shoot Count",
  lag_weight: "Lag Phase Weights", post_veraison_weight: "Post-Veraison Weights",
};
const AVG_LABELS = { cluster_count: "clusters/vine", shoot_count: "shoots/vine", lag_weight: "lb/cluster", post_veraison_weight: "lb/cluster" };

export default function CropEstimates() {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [modalFor, setModalFor] = useState(null);
  const [harvestFor, setHarvestFor] = useState(null);
  const [ranchSel, setRanchSel] = useState("");
  const [scan, setScan] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [queued, setQueued] = useState([]);
  const fileRef = useRef(null);
  const refreshQueue = () => listPendingFormScans().then(setQueued).catch(() => {});
  useEffect(() => { refreshQueue(); }, []);
  const load = async () => setRows((await api.get("/crop/estimates")).data);
  useEffect(() => { load(); }, []);
  if (!rows) return <div className="p-8 font-bold uppercase text-sm">Loading crop estimates…</div>;

  const vineyards = [];
  for (const r of rows) {
    let v = vineyards.find((x) => x.ranch_number === r.ranch_number);
    if (!v) { v = { ranch_number: r.ranch_number, name: r.vineyard_name, blocks: [] }; vineyards.push(v); }
    v.blocks.push(r);
  }
  const shown = ranchSel ? vineyards.filter((v) => v.ranch_number === ranchSel) : vineyards;
  const dl = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const printForm = async (lang = "en") => {
    const qs = [ranchSel ? `ranch_number=${ranchSel}` : "", lang === "es" ? "lang=es" : ""].filter(Boolean).join("&");
    const res = await api.get(`/crop/form${qs ? `?${qs}` : ""}`, { responseType: "blob" });
    dl(res.data, `Sampling-Form-${ranchSel || "All-Ranches"}${lang === "es" ? "-ES" : ""}.pdf`);
  };
  const scanUpload = async (blob, rn) => {
    const fd = new FormData();
    fd.append("ranch_number", rn);
    fd.append("file", blob, "form.jpg");
    const { data } = await api.post("/crop/form/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setScan(data);
  };
  const queueScan = async (file, rn) => {
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    await addPendingFormScan({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ranch_number: rn, image: b64, created_at: new Date().toISOString() });
    await refreshQueue();
  };
  const onScanFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!navigator.onLine) { await queueScan(file, ranchSel); return; }
    setScanBusy(true);
    try { await scanUpload(file, ranchSel); }
    catch (err) {
      if (!err.response) { await queueScan(file, ranchSel); }
      else window.alert(err.response?.data?.detail || "Could not read the form photo — try a flatter, brighter shot.");
    } finally { setScanBusy(false); }
  };
  const processQueued = async () => {
    const item = queued[0];
    if (!item) return;
    setScanBusy(true);
    try {
      const blob = await (await fetch(item.image)).blob();
      const fd = new FormData();
      fd.append("ranch_number", item.ranch_number);
      fd.append("file", blob, "form.jpg");
      const { data } = await api.post("/crop/form/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await removePendingFormScan(item.id);
      await refreshQueue();
      setScan(data);
    } catch (err) {
      if (!err.response) window.alert("Still no connection — the scan stays queued.");
      else { await removePendingFormScan(item.id); await refreshQueue(); window.alert("Could not read the queued photo — it was removed. Re-take a clearer shot."); }
    } finally { setScanBusy(false); }
  };
  const exportRanch = async (rn) => {
    const res = await api.get(`/crop/export${rn ? `?ranch_number=${rn}` : ""}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CropEstimates-${rn || "all"}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="crop-estimates-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("crop.title")}</h1>
          <p className="text-stone-500 text-sm mt-1">{t("crop.sub")}</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="crop-ranch-select" value={ranchSel} onChange={(e) => setRanchSel(e.target.value)}
            className="h-11 px-3 border border-stone-300 bg-white text-sm font-semibold">
            <option value="">{t("crop.all_ranches")}</option>
            {vineyards.map((v) => <option key={v.ranch_number} value={v.ranch_number}>{v.ranch_number} · {v.name}</option>)}
          </select>
          <button data-testid="crop-form-btn" onClick={() => printForm("en")}
            className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><Printer size={14} /> {t("crop.sampling_form")}</button>
          <button data-testid="crop-form-es-btn" onClick={() => printForm("es")}
            className="h-11 px-4 bg-[#7C9673] text-white font-bold uppercase text-xs flex items-center gap-2"><Printer size={14} /> Formulario ES</button>
          <button data-testid="crop-scan-btn" onClick={() => { if (!ranchSel) { window.alert("Pick a ranch from the dropdown first so the scan can match its blocks."); return; } fileRef.current?.click(); }} disabled={scanBusy}
            className="h-11 px-4 bg-[#7C9673] text-white font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40">
            {scanBusy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {t("crop.scan_form")}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScanFile} data-testid="crop-scan-input" />
          <button data-testid="crop-export-all-btn" onClick={() => exportRanch(ranchSel || null)} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><FileDown size={14} /> {t("crop.export")}</button>
          <button data-testid="crop-summary-pdf-btn" onClick={async () => {
            const res = await api.get(`/crop/summary-pdf${ranchSel ? `?ranch_number=${ranchSel}` : ""}`, { responseType: "blob" });
            dl(res.data, `CropEstimateSummary-${ranchSel || "all"}.pdf`);
          }} className="h-11 px-4 border border-[#1F3D34] text-[#1F3D34] bg-white font-bold uppercase text-xs flex items-center gap-2"><Printer size={14} /> {t("crop.print_summary")}</button>
        </div>
      </div>
      {queued.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-500 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="scan-queue-banner">
          <div className="text-sm font-bold text-amber-900 flex items-center gap-2"><CloudUpload size={16} /> {queued.length} form scan{queued.length > 1 ? "s" : ""} queued offline (Ranch {queued.map((q) => q.ranch_number).join(", ")})</div>
          <button data-testid="process-queued-btn" onClick={processQueued} disabled={scanBusy}
            className="h-10 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40">
            {scanBusy ? "Uploading…" : "Process Now"}
          </button>
        </div>
      )}
      {shown.map((v) => {
        const totAcres = Math.round(v.blocks.reduce((s, b) => s + (b.acres || 0), 0) * 10) / 10;
        const totTons = Math.round(v.blocks.reduce((s, b) => s + (b.total_tons || 0), 0) * 10) / 10;
        const totActual = Math.round(v.blocks.reduce((s, b) => s + (b.actual_tons || 0), 0) * 10) / 10;
        const estBlocks = v.blocks.filter((b) => b.total_tons != null).length;
        return (
        <div key={v.ranch_number} className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-bold uppercase tracking-wide text-sm text-stone-600">Ranch {v.ranch_number} · {v.name}</div>
            <button data-testid={`crop-export-${v.ranch_number}`} onClick={() => exportRanch(v.ranch_number)} className="text-xs font-bold uppercase text-[#1F3D34] underline flex items-center gap-1"><FileDown size={12} /> {t("crop.export_ranch")}</button>
          </div>
          <div className="bg-white border-2 border-[#1F3D34] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid={`ranch-totals-${v.ranch_number}`}>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Blocks</div><div className="font-bold">{v.blocks.length} ({estBlocks} estimated)</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Total Acres</div><div className="font-bold">{totAcres}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Ranch Est. Tons</div><div className="font-bold text-lg" data-testid={`ranch-total-tons-${v.ranch_number}`}>{totTons || "—"}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Actual Tons</div><div className="font-bold text-lg">{totActual || "—"}</div></div>
          </div>
          {v.blocks.map((b) => <BlockCard key={b.block_id} b={b} onSample={() => setModalFor(b)} onHarvest={() => setHarvestFor(b)} reload={load} />)}
        </div>
        );
      })}
      {rows.length === 0 && <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm">No blocks — add blocks under Ranches first</div>}
      {modalFor && <SampleModal b={modalFor} onClose={() => setModalFor(null)} onDone={() => { setModalFor(null); load(); }} />}
      {harvestFor && <HarvestModal b={harvestFor} onClose={() => setHarvestFor(null)} onDone={() => { setHarvestFor(null); load(); }} />}
      {scan && <ScanReviewModal scan={scan} onClose={() => setScan(null)} onDone={() => { setScan(null); load(); }} />}
    </div>
  );
}

function Stat({ label, v, unit, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-bold uppercase text-stone-400">{label}</div>
      <div className="font-bold text-lg">{v ?? "—"}{v != null && unit ? <span className="text-xs font-semibold text-stone-500"> {unit}</span> : null}</div>
    </div>
  );
}

function TrendChart({ b }) {
  const wts = (b.samples || [])
    .filter((s) => s.sample_type === "lag_weight" || s.sample_type === "post_veraison_weight")
    .sort((x, y) => (x.date || "").localeCompare(y.date || ""));
  if (wts.length < 2 || !b.clusters_per_vine || !b.vines_per_acre) return null;
  const pts = wts.map((s) => {
    const lb = s.sample_type === "lag_weight" ? s.average * b.lag_multiplier : s.average;
    return { date: s.date, lag: s.sample_type === "lag_weight",
      tpa: Math.round((b.clusters_per_vine * lb * b.vines_per_acre) / 2000 * 100) / 100 };
  });
  const W = 560, H = 120, PX = 40, PY = 22;
  const vals = pts.map((p) => p.tpa);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => PX + (i * (W - PX * 2)) / Math.max(pts.length - 1, 1);
  const y = (v) => H - PY - ((v - min) / span) * (H - PY * 2);
  return (
    <div className="mx-4 mb-3 bg-stone-50 border border-stone-200 p-3 overflow-x-auto" data-testid={`trend-chart-${b.block_id}`}>
      <div className="text-[10px] font-bold uppercase text-stone-400 mb-1">Estimate Trend — tons/acre by weight sample (hollow = lag × mult, solid = post-veraison)</div>
      <svg width={W} height={H} className="max-w-full">
        <polyline fill="none" stroke="#1F3D34" strokeWidth="2"
          points={pts.map((p, i) => `${x(i)},${y(p.tpa)}`).join(" ")} />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.tpa)} r="4.5" fill={p.lag ? "white" : "#7C9673"} stroke={p.lag ? "#D97706" : "#1F3D34"} strokeWidth="2" />
            <text x={x(i)} y={y(p.tpa) - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F3D34">{p.tpa}</text>
            <text x={x(i)} y={H - 4} textAnchor="middle" fontSize="8.5" fill="#78716C">{(p.date || "").slice(5)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function BlockCard({ b, onSample, onHarvest, reload }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [mult, setMult] = useState(String(b.lag_multiplier));
  const [savingMult, setSavingMult] = useState(false);
  const setMultiplier = async (val) => {
    if (!Number(val) || Number(val) === b.lag_multiplier) return;
    setSavingMult(true);
    try { await api.put(`/crop/settings/${b.block_id}`, { lag_multiplier: Number(val) }); await reload(); }
    finally { setSavingMult(false); }
  };
  const delSample = async (s) => {
    if (!window.confirm(`Delete this ${TYPE_LABELS[s.sample_type]} sample from ${s.date}?`)) return;
    await api.delete(`/crop/samples/${s.id}`);
    await reload();
  };
  const delHarvest = async (h) => {
    if (!window.confirm(`Delete harvest entry of ${h.actual_tons} tons on ${h.date}?`)) return;
    await api.delete(`/crop/harvest/${h.id}`);
    await reload();
  };
  return (
    <div className="bg-white border border-stone-200" data-testid={`crop-block-${b.block_id}`}>
      <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-stone-100">
        <div>
          <div className="font-bold flex items-center gap-2"><Grape size={16} className="text-[#7C9673]" /> {b.block} <span className="text-stone-400 font-normal text-sm">{b.variety || ""}</span></div>
          <div className="text-xs text-stone-500">{b.acres ?? "—"} ac · {b.vines_per_acre ? `${b.vines_per_acre} vines/ac` : <span className="text-amber-700 font-bold">vines/acre not set — add it under Ranches</span>}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid={`record-harvest-${b.block_id}`} onClick={onHarvest} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><Scale size={14} /> {t("crop.record_harvest")}</button>
          <button data-testid={`add-sample-${b.block_id}`} onClick={onSample} className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><Plus size={14} /> {t("crop.add_sample")}</button>
        </div>
      </div>
      <div className="p-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
        <Stat label="Clusters / Vine" v={b.clusters_per_vine} testId={`cpv-${b.block_id}`} />
        <Stat label="Shoots / Vine" v={b.shoots_per_vine} />
        <Stat label="Clusters / Shoot" v={b.clusters_per_shoot} testId={`cps-${b.block_id}`} />
        <Stat label="Lag Avg" v={b.lag_avg_lb} unit="lb" />
        <div>
          <div className="text-[10px] font-bold uppercase text-stone-400">Lag Multiplier</div>
          <div className="flex items-center gap-1">
            <input data-testid={`mult-input-${b.block_id}`} value={mult} onChange={(e) => setMult(e.target.value)} onBlur={() => setMultiplier(mult)}
              className="w-16 h-9 px-2 border border-stone-300 font-bold" />
            {savingMult && <Loader2 size={13} className="animate-spin" />}
          </div>
        </div>
        <Stat label={b.method === "post_veraison" ? `Current Wt (PV ${b.pv_latest_date ? b.pv_latest_date.slice(5) : ""})` : "Projected Wt (lag × mult)"}
          v={b.current_lb} unit="lb" testId={`current-lb-${b.block_id}`} />
      </div>
      {b.tons_per_acre != null ? (
        <div className="mx-4 mb-3 bg-[#1F3D34] text-white p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid={`estimate-strip-${b.block_id}`}>
          <div><div className="text-[10px] font-bold uppercase text-white/50">Lbs / Vine</div><div className="font-bold">{b.lbs_per_vine}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-white/50">Lbs / Acre</div><div className="font-bold">{Number(b.lbs_per_acre).toLocaleString()}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-white/50">Tons / Acre</div><div className="font-bold text-lg" data-testid={`tpa-${b.block_id}`}>{b.tons_per_acre}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-white/50">Total Tons ({b.acres} ac)</div><div className="font-bold text-lg" data-testid={`total-tons-${b.block_id}`}>{b.total_tons}</div></div>
        </div>
      ) : (
        <div className="mx-4 mb-3 text-xs text-stone-500 font-semibold" data-testid={`estimate-missing-${b.block_id}`}>
          Estimate needs: {[!b.clusters_per_vine && "cluster count", !b.current_lb && "cluster weights", !b.vines_per_acre && "vines/acre (Ranches)"].filter(Boolean).join(" · ")}
        </div>
      )}
      <TrendChart b={b} />
      {b.actual_tons != null && (
        <div className="mx-4 mb-3 border-2 border-[#7C9673] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm items-center" data-testid={`harvest-strip-${b.block_id}`}>
          <div><div className="text-[10px] font-bold uppercase text-stone-400">Actual Harvest</div><div className="font-bold text-lg">{b.actual_tons} tons</div></div>
          <div><div className="text-[10px] font-bold uppercase text-stone-400">Estimate</div><div className="font-bold">{b.total_tons ?? "—"} tons</div></div>
          <div>
            <div className="text-[10px] font-bold uppercase text-stone-400">Variance</div>
            {b.variance_pct != null ? (
              <span className={`font-bold px-2 py-0.5 text-sm ${Math.abs(b.variance_pct) <= 10 ? "bg-[#7C9673] text-white" : "bg-red-600 text-white"}`} data-testid={`variance-${b.block_id}`}>
                {b.variance_pct > 0 ? "+" : ""}{b.variance_pct}%
              </span>
            ) : "—"}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase text-stone-400">Suggested Multiplier</div>
            {b.suggested_multiplier != null ? (
              <div className="flex items-center gap-2">
                <span className="font-bold">{b.suggested_multiplier}</span>
                {b.suggested_multiplier !== b.lag_multiplier && (
                  <button data-testid={`apply-mult-${b.block_id}`} onClick={() => { setMult(String(b.suggested_multiplier)); setMultiplier(b.suggested_multiplier); }}
                    className="h-8 px-2 bg-[#1F3D34] text-white font-bold uppercase text-[10px]">Apply</button>
                )}
              </div>
            ) : "—"}
          </div>
        </div>
      )}
      {(b.samples.length > 0 || (b.harvests || []).length > 0) && (
        <button onClick={() => setOpen(!open)} className="w-full px-4 pb-3 text-left text-xs font-bold uppercase text-stone-500 flex items-center gap-1" data-testid={`toggle-samples-${b.block_id}`}>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {b.samples.length} sample{b.samples.length === 1 ? "" : "s"}
          {(b.harvests || []).length > 0 ? ` · ${b.harvests.length} harvest entr${b.harvests.length === 1 ? "y" : "ies"}` : ""}
          {b.pv_sample_count > 0 ? ` · latest post-veraison drives the estimate` : ""}
        </button>
      )}
      {open && (
        <div className="px-4 pb-4 space-y-1">
          {b.samples.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs bg-stone-50 border border-stone-200 p-2" data-testid={`sample-row-${s.id}`}>
              <span className="font-mono font-bold w-20">{s.date}</span>
              <span className="font-bold">{TYPE_LABELS[s.sample_type]}</span>
              <span className="text-stone-600">{s.total_value} ÷ {s.units_counted} = <b>{s.average} {AVG_LABELS[s.sample_type]}</b></span>
              {s.notes && <span className="text-stone-400">{s.notes}</span>}
              <button onClick={() => delSample(s)} className="ml-auto text-red-600" data-testid={`del-sample-${s.id}`}><Trash2 size={13} /></button>
            </div>
          ))}
          {(b.harvests || []).map((h) => (
            <div key={h.id} className="flex flex-wrap items-center gap-2 text-xs bg-[#7C9673]/10 border border-[#7C9673]/40 p-2" data-testid={`harvest-row-${h.id}`}>
              <span className="font-mono font-bold w-20">{h.date}</span>
              <span className="font-bold">Harvest</span>
              <span className="text-stone-700"><b>{h.actual_tons} tons</b></span>
              {h.notes && <span className="text-stone-400">{h.notes}</span>}
              <button onClick={() => delHarvest(h)} className="ml-auto text-red-600" data-testid={`del-harvest-${h.id}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScanReviewModal({ scan, onClose, onDone }) {
  const [date, setDate] = useState(scan.date);
  const [rows, setRows] = useState(scan.rows.map((r) => ({ ...r, include: r.matched })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upd = (i, u) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...u } : r)));
  const n = (v) => (Number(v) > 0 ? Number(v) : null);
  const saveAll = async () => {
    setBusy(true); setErr("");
    try {
      let saved = 0;
      for (const r of rows) {
        if (!r.include || !r.block_id) continue;
        const base = { block_id: r.block_id, date, notes: r.notes || null };
        if (n(r.total_clusters)) { await api.post("/crop/samples", { ...base, sample_type: "cluster_count", units_counted: n(r.vines_counted) || 40, total_value: n(r.total_clusters) }); saved++; }
        if (n(r.total_shoots)) { await api.post("/crop/samples", { ...base, sample_type: "shoot_count", units_counted: n(r.vines_counted) || 40, total_value: n(r.total_shoots) }); saved++; }
        if (n(r.total_weight_lb) && r.weight_type) { await api.post("/crop/samples", { ...base, sample_type: r.weight_type === "lag" ? "lag_weight" : "post_veraison_weight", units_counted: n(r.clusters_weighed) || 30, total_value: n(r.total_weight_lb) }); saved++; }
      }
      if (!saved) { setErr("Nothing to save — check rows, blocks and weight types."); setBusy(false); return; }
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save samples"); setBusy(false); }
  };
  const inp = "w-full h-9 px-2 border border-stone-300 text-xs";
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-3xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="scan-review-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Scanned Samples — Review Before Saving</h3>
          <button onClick={onClose} data-testid="scan-close"><X size={20} /></button>
        </div>
        <div className="flex items-center gap-3">
          <Field label="Sample Date"><input type="date" data-testid="scan-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 px-3 border border-stone-300" /></Field>
          <div className="text-xs text-stone-500 font-semibold">Verify every number against the paper form — fix anything the scan misread.</div>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className={`border p-3 space-y-2 ${r.include ? "border-[#1F3D34]" : "border-stone-200 opacity-60"}`} data-testid={`scan-row-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <input type="checkbox" checked={r.include} onChange={(e) => upd(i, { include: e.target.checked })} className="w-5 h-5 accent-[#1F3D34]" data-testid={`scan-include-${i}`} />
                <select value={r.block_id || ""} onChange={(e) => upd(i, { block_id: e.target.value, matched: true })} className="h-9 px-2 border border-stone-300 text-xs font-bold" data-testid={`scan-block-${i}`}>
                  <option value="">— Match block —</option>
                  {scan.blocks.map((b) => <option key={b.id} value={b.id}>{b.block}</option>)}
                </select>
                {!r.matched && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-2 py-0.5">Read as "{r.block}" — pick the block</span>}
                <select value={r.weight_type || ""} onChange={(e) => upd(i, { weight_type: e.target.value || null })} className="h-9 px-2 border border-stone-300 text-xs ml-auto">
                  <option value="">No weight sample</option>
                  <option value="lag">Lag phase</option>
                  <option value="post_veraison">Post-veraison</option>
                </select>
              </div>
              <div className="grid grid-cols-5 gap-2">
                <Field label="Vines"><input value={r.vines_counted ?? ""} onChange={(e) => upd(i, { vines_counted: e.target.value })} className={inp} /></Field>
                <Field label="Clusters"><input data-testid={`scan-clusters-${i}`} value={r.total_clusters ?? ""} onChange={(e) => upd(i, { total_clusters: e.target.value })} className={inp} /></Field>
                <Field label="Shoots"><input value={r.total_shoots ?? ""} onChange={(e) => upd(i, { total_shoots: e.target.value })} className={inp} /></Field>
                <Field label="Cl. Weighed"><input value={r.clusters_weighed ?? ""} onChange={(e) => upd(i, { clusters_weighed: e.target.value })} className={inp} /></Field>
                <Field label="Total Wt (lb)"><input value={r.total_weight_lb ?? ""} onChange={(e) => upd(i, { total_weight_lb: e.target.value })} className={inp} /></Field>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-sm text-stone-500 font-semibold p-4 text-center">No filled rows detected — try a clearer photo.</div>}
        </div>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-semibold" data-testid="scan-error">{err}</div>}
        <button data-testid="scan-save-btn" onClick={saveAll} disabled={busy || !rows.some((r) => r.include && r.block_id)}
          className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} Save Samples To Crop Estimates
        </button>
      </div>
    </div>
  );
}

function SampleModal({ b, onClose, onDone }) {
  const [mode, setMode] = useState("counts");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [vines, setVines] = useState("40");
  const [clusters, setClusters] = useState("");
  const [shoots, setShoots] = useState("");
  const [units, setUnits] = useState("30");
  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const n = (v) => (Number(v) > 0 ? Number(v) : null);
  const cpv = n(vines) && n(clusters) ? Math.round((Number(clusters) / Number(vines)) * 100) / 100 : null;
  const spv = n(vines) && n(shoots) ? Math.round((Number(shoots) / Number(vines)) * 100) / 100 : null;
  const wAvg = n(units) && n(weight) ? Math.round((Number(weight) / Number(units)) * 1000) / 1000 : null;
  const ready = mode === "counts" ? !!(cpv || spv) : !!wAvg;
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (mode === "counts") {
        if (cpv) await api.post("/crop/samples", { block_id: b.block_id, sample_type: "cluster_count", date, units_counted: Number(vines), total_value: Number(clusters), notes: notes || null });
        if (spv) await api.post("/crop/samples", { block_id: b.block_id, sample_type: "shoot_count", date, units_counted: Number(vines), total_value: Number(shoots), notes: notes || null });
      } else {
        await api.post("/crop/samples", { block_id: b.block_id, sample_type: mode, date, units_counted: Number(units), total_value: Number(weight), notes: notes || null });
      }
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save sample"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-md p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="sample-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Sample — {b.block}</h3>
          <button onClick={onClose} data-testid="sample-close"><X size={20} /></button>
        </div>
        <div className="flex flex-wrap gap-2">
          {[["counts", "Counts — Clusters & Shoots"], ["lag_weight", WT.lag_weight.label], ["post_veraison_weight", WT.post_veraison_weight.label]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`sample-type-${k}`} onClick={() => setMode(k)}
              className={`h-11 px-3 text-xs font-bold uppercase border ${mode === k ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>{l}</button>
          ))}
        </div>
        <Field label="Sample Date"><input type="date" data-testid="sample-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        {mode === "counts" ? (
          <>
            <Field label="Vines Counted (standard 40)"><input data-testid="sample-vines" value={vines} onChange={(e) => setVines(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Total Clusters"><input data-testid="sample-clusters" value={clusters} onChange={(e) => setClusters(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
              <Field label="Total Shoots"><input data-testid="sample-shoots" value={shoots} onChange={(e) => setShoots(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
            </div>
            {(cpv || spv) && (
              <div className="bg-stone-50 border border-stone-200 p-3 text-sm" data-testid="sample-avg-preview">
                {cpv && <>Clusters/vine: <b>{cpv}</b></>}{cpv && spv && " · "}
                {spv && <>Shoots/vine: <b>{spv}</b></>}
                {cpv && spv && <> · Clusters/shoot: <b>{Math.round((cpv / spv) * 100) / 100}</b></>}
              </div>
            )}
          </>
        ) : (
          <>
            {mode === "post_veraison_weight" && <div className="text-[11px] text-stone-500 font-semibold">Take multiple samples as harvest nears — the latest one becomes the current cluster weight.</div>}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Clusters Weighed (standard 30)"><input data-testid="sample-units" value={units} onChange={(e) => setUnits(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
              <Field label="Total Weight (lbs)"><input data-testid="sample-total" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
            </div>
            {wAvg && <div className="bg-stone-50 border border-stone-200 p-3 text-sm" data-testid="sample-avg-preview">Average: <b>{wAvg} lb/cluster</b></div>}
          </>
        )}
        <Field label="Notes (optional)"><input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-semibold" data-testid="sample-error">{err}</div>}
        <button data-testid="sample-submit" onClick={submit} disabled={busy || !ready}
          className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} Save Sample{mode === "counts" && cpv && spv ? "s" : ""}
        </button>
      </div>
    </div>
  );
}

function HarvestModal({ b, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tons, setTons] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/crop/harvest", { block_id: b.block_id, date, actual_tons: Number(tons), notes: notes || null });
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save harvest"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-sm p-5 space-y-3" data-testid="harvest-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Harvest — {b.block}</h3>
          <button onClick={onClose} data-testid="harvest-close"><X size={20} /></button>
        </div>
        <div className="text-[11px] text-stone-500 font-semibold">Multiple picks add together. Entries compare against the estimate and suggest a tuned lag multiplier for next season.</div>
        <Field label="Pick Date"><input type="date" data-testid="harvest-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Actual Tons"><input data-testid="harvest-tons" value={tons} onChange={(e) => setTons(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Notes (optional)"><input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-semibold" data-testid="harvest-error">{err}</div>}
        <button data-testid="harvest-submit" onClick={submit} disabled={busy || !(Number(tons) > 0)}
          className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} Save Harvest
        </button>
      </div>
    </div>
  );
}
